import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
interface Tier {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  isPopular?: boolean;
  isPremium?: boolean;
}
const tiers: Tier[] = [{
  id: 'tier-1',
  name: 'Community Member',
  price: 3000,
  description: 'Your foundation in the sustainability ecosystem',
  features: ['Access to the private member community platform', 'Curated career resource library (templates, frameworks, guides)', 'Monthly sustainability industry digest & newsletter', 'Member directory — search and connect with peers', 'Exclusive job board and opportunities feed', 'Invitations to free virtual events and community roundtables']
}, {
  id: 'tier-2',
  name: 'Group Mentorship',
  price: 20000,
  description: 'Accelerate your growth through expert-led group sessions',
  isPopular: true,
  features: ['Everything in Tier 1 — Community Member', 'Monthly group mentorship calls with select mentors', 'Small-cohort format (max 20 participants)', 'Session recordings and resource packs', 'Direct Q&A with sustainability executives', 'Peer accountability pods and cross-industry collaborations', 'Priority access to workshops, panels, and live events']
}, {
  id: 'tier-3',
  name: 'Elite 1-on-1 Mentorship',
  price: 100000,
  description: 'Bespoke guidance for high-performance sustainability leaders',
  isPremium: true,
  features: ['Everything in Tiers 1 & 2', 'Monthly one-on-one mentorship sessions with a matched leader', 'Bi-annual career clarity and strategy calls', 'Personalized growth roadmap and accountability check-ins', 'Introduction to curated networks, boards, and speaking opportunities', 'Priority review of CVs, proposals, reports, and thought leadership', 'VIP access to in-person events and exclusive member dinners']
}];
interface PricingProps {
  onSelectTier: (tier: Tier) => void;
}
export function Pricing({
  onSelectTier
}: PricingProps) {
  return <section id="memberships" className="py-24 px-6 bg-cream" data-id="element-64">
      <div className="max-w-7xl mx-auto" data-id="element-65">
        <div className="text-center mb-20" data-id="element-66">
          <h2 className="font-serif text-4xl md:text-5xl text-forest mb-6" data-id="element-67">
            Membership Tiers
          </h2>
          <p className="text-charcoal-light text-lg max-w-2xl mx-auto" data-id="element-68">
            Designed to meet professionals at different stages of their journey
            and investment capacity.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-69">
          {tiers.map((tier, index) => <motion.div key={tier.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className={`relative flex flex-col h-full bg-white border ${tier.isPremium ? 'border-gold shadow-xl shadow-gold/10' : tier.isPopular ? 'border-forest shadow-xl shadow-forest/5' : 'border-sage/20'} ${tier.isPremium ? 'lg:-mt-4 lg:mb-4' : ''}`} data-id="element-70">
              {tier.isPopular && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-forest text-cream text-xs font-bold tracking-wider uppercase py-1 px-4" data-id="element-71">
                  Most Popular
                </div>}
              {tier.isPremium && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gold text-forest text-xs font-bold tracking-wider uppercase py-1 px-4" data-id="element-72">
                  Premium
                </div>}

              <div className={`p-8 border-b ${tier.isPremium ? 'bg-forest text-cream border-forest-light' : 'border-sage/20'}`} data-id="element-73">
                <h3 className={`font-serif text-2xl mb-2 ${tier.isPremium ? 'text-gold' : 'text-forest'}`} data-id="element-74">
                  {tier.name}
                </h3>
                <p className={`text-sm mb-6 h-10 ${tier.isPremium ? 'text-sage-light' : 'text-charcoal-light'}`} data-id="element-75">
                  {tier.description}
                </p>
                <div className="flex items-baseline gap-1" data-id="element-76">
                  <span className="text-4xl font-serif" data-id="element-77">
                    ₦{tier.price.toLocaleString()}
                  </span>
                  <span className={`text-sm ${tier.isPremium ? 'text-sage-light' : 'text-charcoal-light'}`} data-id="element-78">
                    / Year
                  </span>
                </div>
              </div>

              <div className="p-8 flex-grow flex flex-col" data-id="element-79">
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-80">
                  {tier.features.map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-81">
                      <CheckIcon className={`shrink-0 mt-0.5 ${tier.isPremium ? 'text-gold' : 'text-forest'}`} size={18} data-id="element-82" />
                      <span className="text-sm text-charcoal leading-relaxed" data-id="element-83">
                        {feature}
                      </span>
                    </li>)}
                </ul>

                <button onClick={() => onSelectTier(tier)} className={`w-full py-4 text-center font-medium transition-colors ${tier.isPremium ? 'bg-gold text-forest hover:bg-gold-light' : tier.isPopular ? 'bg-forest text-cream hover:bg-forest-light' : 'bg-cream-dark text-forest hover:bg-sage/20'}`} data-id="element-84">
                  Join This Tier
                </button>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}